import React from 'react';
import { AnimationSettingsModel, DialogComponent, PositionDataModel } from '@syncfusion/ej2-react-popups';
import { FabComponent } from '@syncfusion/ej2-react-buttons';
import FacilityMap from './FacilityMap';
import ViewerStore from '../stores/ViewerStore';
import viewerActions from '../actions/ViewerActions';
interface FacilityMiniMapProps {
    siteMap: ISiteMap;
    views?: IView360Detail[];
    onViewMarkerClicked?: (view: IView360Detail, index: number) => void;
    onViewMarkerHighlighted?: (view: IView360Detail, index: number, highlighted: boolean) => void;
    activeView: IView360Detail;
    highlightedView: IView360Detail;
    targetId: string;
    viewerDirectionDegrees: number;
    knowledgeViewOpened: boolean;
    pointCloudAvailable: boolean;
    title?: string;
    overridePosition?: IVector3D;
}

const FacilityMiniMap: React.FC<FacilityMiniMapProps> = (props) => {
    const DefaultMiniMapWidth = 350;
    const DefaultMiniMapHeight = 300;

    const settings: AnimationSettingsModel = { effect: 'Zoom', duration: 400, delay: 0 };

    // var speedDialClass = ViewerStore.isOpenPointCloud() && ViewerStore.getSelectedAssembly() ? 'mr-12rem' : props.knowledgeViewOpened ? "kv-mapButton" : props.pointCloudAvailable ? "mapButtonShowPoints" : "mapButton";
    const [resizeCount, setResizeCount] = React.useState(0);
    const [mimiMapSize, setMimiMapSize] = React.useState(ViewerStore.miniMapSize());
    const dialogInstance = React.useRef<DialogComponent>(null);
    const [fullScreen, setFullScreen] = React.useState(ViewerStore.miniMapFullScreen());

    const [maxState, setmaxState] = React.useState(false);
    const [minState, setminState] = React.useState(false);
    const [showMap, setShowMap] = React.useState<boolean>(ViewerStore.isMiniMapOpen());
    const [dialogOldPositions, setdialogOldPositions] = React.useState<PositionDataModel>();
    const [dialogPositions, setdialogPositions] = React.useState<PositionDataModel>(ViewerStore.miniMapPositions());

    const mountedRef = React.useRef(true);
    React.useEffect(() => () => { mountedRef.current = false; }, []);

    const getHostElement = React.useCallback(() => {
        const hostId = props.targetId?.replace('#', '');
        return hostId ? document.getElementById(hostId) : null;
    }, [props.targetId]);

    const getDefaultDialogPosition = React.useCallback((hostElement?: HTMLElement | null): PositionDataModel => {
        const element = hostElement ?? getHostElement();
        const hostHeight = Math.round(element?.getBoundingClientRect().height ?? 0);
        const dialogHeight = mimiMapSize?.height ?? DefaultMiniMapHeight;

        return {
            X: 5,
            Y: Math.max(5, hostHeight - dialogHeight - 5)
        };
    }, [DefaultMiniMapHeight, getHostElement, mimiMapSize]);

    const applyDialogPosition = React.useCallback((position: PositionDataModel) => {
        const dialog = dialogInstance.current;
        if (dialog) {
            dialog.position = position;
            dialog.dataBind();
        }

        const dialogEle = $('#mini-map-dialog');
        dialogEle.css("left", `${position.X}px`);
        dialogEle.css("top", `${position.Y}px`);
    }, []);

    React.useEffect(() => {
        // (kept to preserve original dependency; does nothing intentionally)
    }, [props.viewerDirectionDegrees]);

    React.useEffect(() => {
        if (!showMap || dialogPositions) {
            return;
        }

        const defaultPosition = getDefaultDialogPosition();
        setdialogPositions(defaultPosition);
        viewerActions.setMiniMapPositions(defaultPosition);
    }, [dialogPositions, getDefaultDialogPosition, showMap]);

    // SINGLE definition of handleDialogResize (guarded + stable)
    const handleDialogResize = React.useCallback((e, notStoreSize=false) => {
        const el = dialogInstance.current?.element;
        if (!mountedRef.current || !el) return; // guard: EJ2 may call during teardown
        $("#mini-map-content")
            .width(el.clientWidth)
            .height(el.clientHeight - 45);
        setResizeCount((n) => n + 1);
        if (!notStoreSize) viewerActions.setMiniMapSize({ width: el.clientWidth, height: el.clientHeight});
    }, []);

    const onViewMarkerClicked = (view: IView360Detail, index: number) => {
        if (props.activeView !== view && fullScreen) {
            onMaxButtonClick();
        }
        props.onViewMarkerClicked && props.onViewMarkerClicked(view, index);
    };

    const header = () => {
        let maxClass = "e-icons sf-icon-Maximize";
        let maxTitle = InovxLoader.t('facility:action.maximize');
        const minTitle = InovxLoader.t('facility:action.restore');
        const closeTitle = InovxLoader.t('facility:action.close');
        if (maxState) {
            maxClass = "e-icons sf-icon-Restore";
            maxTitle = InovxLoader.t('facility:action.restore');
        }

        let minClass = "e-icons sf-icon-Minimize";
        const closeClass = "e-icons sf-icon-Close";
        if (minState) {
            minClass = "e-icons sf-icon-Restore";
        }
        return (
            <div className="d-flex j-csb">
                <span className="project-title" id="project-title" title={props.title}>{props.title}</span>
                <span>
                    <span className={closeClass} id='close-btn' title={closeTitle} onClick={onCloseButtonClick}></span>
                    <span className={maxClass} id='max-btn' title={maxTitle} onClick={onMaxButtonClick}></span>
                </span>
                {/*<span className={minClass} id='min-btn' title={minTitle} onClick={onMinButtonClick}></span>*/}
            </div>
        );
    };

    const onBeforeOpen = (args: any) => {
        const element = getHostElement();
        if (!element) return;

        const rect = element.getBoundingClientRect();
        args.maxHeight = rect.height;
        if (dialogInstance?.current?.element?.classList?.contains('dialog-maximized')) {
            applyDialogPosition({ X: 0, Y: 0 });
        } else {
            const position = dialogPositions ?? getDefaultDialogPosition(element);

            if (!dialogPositions) {
                setdialogPositions(position);
                viewerActions.setMiniMapPositions(position);
            }

            applyDialogPosition(position);

            const dialogEle = $('#mini-map-dialog');
            if (mimiMapSize) {
                $("#mini-map-content").width(mimiMapSize.width).height(mimiMapSize.height - 45);
                dialogEle.css("width", mimiMapSize.width + "px");
                dialogEle.css("height", mimiMapSize.height + "px");
            }
        }
    };

    const onDragStop = (_args: any) => {
        const dlg = dialogInstance.current?.element;
        if (!dlg) return;
        const dialogEle = $('#mini-map-dialog');
        if (!dlg.classList.contains('dialog-maximized')) {
            const pos = { X: dialogEle.position().left, Y: dialogEle.position().top };
            viewerActions.setMiniMapPositions(pos);
        }
    };

    const applyFullScreenState = () => {
        const dlg = dialogInstance.current?.element;
        if (!dlg) return;

        const maximizeIcon = dlg.querySelector('#max-btn') as HTMLElement;
        const dialogEle = $('#mini-map-dialog');

        if (ViewerStore.miniMapFullScreen()) {
            setdialogOldPositions({ X: dialogEle.position().left, Y: dialogEle.position().top });
            dlg.classList.add('dialog-maximized');
            dialogInstance.current!.show(true);
            maximizeIcon?.setAttribute('title', InovxLoader.t('facility:action.restore'));
            if (maximizeIcon) maximizeIcon.className = "e-icons sf-icon-Restore";
            dlg.querySelector('.e-dlg-content')?.classList.remove('hide-content');
            dialogEle.css("left", 0);
            dialogEle.css("top", 0);
            $('.e-dialog .e-south-east').css("display", "none");
            setmaxState(true);
            handleDialogResize(null, true);
        }
    }


    const onMaxButtonClick = () => {
        const dlg = dialogInstance.current?.element;
        if (!dlg) return;

        const maximizeIcon = dlg.querySelector('#max-btn') as HTMLElement;
        const dialogEle = $('#mini-map-dialog');

        if (dlg.classList.contains('dialog-minimized')) {
            const minBtn = dlg.querySelector('#min-btn') as HTMLElement;
            if (minBtn) {
                minBtn.className = "e-icons sf-icon-Minimize";
                minBtn.setAttribute('title', 'Minimize');
            }
        }

        if (!dlg.classList.contains('dialog-maximized')) {
            setdialogOldPositions({ X: dialogEle.position().left, Y: dialogEle.position().top });
            dlg.classList.add('dialog-maximized');
            dialogInstance.current!.show(true);
            maximizeIcon?.setAttribute('title', InovxLoader.t('facility:action.restore'));
            if (maximizeIcon) maximizeIcon.className = "e-icons sf-icon-Restore";
            dlg.querySelector('.e-dlg-content')?.classList.remove('hide-content');
            dialogEle.css("left", 0);
            dialogEle.css("top", 0);
            $('.e-dialog .e-south-east').css("display", "none");
            setmaxState(true);
            viewerActions.setMiniMapFullScreen(true);
        } else {
            dlg.classList.remove('dialog-maximized');
            dialogInstance.current!.show(false);
            maximizeIcon?.setAttribute('title', InovxLoader.t('facility:action.maximize'));
            if (maximizeIcon) maximizeIcon.className = "e-icons sf-icon-Maximize";
            dlg.querySelector('.e-dlg-content')?.classList.remove('hide-content');
            setdialogOldPositions({ X: dialogEle.position().left, Y: dialogEle.position().top });
            $('.e-dialog .e-south-east').css("display", "block");
            dialogInstance.current!.dataBind();
            setmaxState(false);
            viewerActions.setMiniMapFullScreen(false);
        }
        handleDialogResize(null, true);
    };

    const onMinButtonClick = (_e: any) => {
        const dlg = dialogInstance.current?.element;
        if (!dlg) return;

        const minimizeIcon = dlg.querySelector('#min-btn') as HTMLElement;
        const height: number = $(props.targetId).height();
        const dialogEle = $('#mini-map-dialog');

        if (!dlg.classList.contains('e-dlg-fullscreen')) {
            if (!dlg.classList.contains('dialog-minimized')) {
                setdialogOldPositions({ X: dialogEle.position().left, Y: dialogEle.position().top });
                dlg.classList.add('dialog-minimized');
                dlg.classList.remove('dialog-maximized');
                dlg.querySelector('.e-dlg-content')?.classList.add('hide-content');
                dialogInstance.current!.height = 30;
                if (minimizeIcon) {
                    minimizeIcon.className = "e-icons sf-icon-Restore";
                    minimizeIcon.setAttribute('title', 'Restore');
                }
                $('.e-dialog .e-south-east').css("display", "none");
                dialogEle.css("left", "5px");
                dialogEle.css("top", height - 35);
                dialogInstance.current!.dataBind();
                setminState(true);
                setFullScreen(false);
            } else {
                dlg.classList.remove('dialog-minimized');
                dlg.querySelector('.e-dlg-content')?.classList.remove('hide-content');
                if (minimizeIcon) {
                    minimizeIcon.className = "e-icons sf-icon-Minimize";
                    minimizeIcon.setAttribute('title', 'Minimize');
                }
                dialogEle.css("left", String(dialogOldPositions?.X ?? 0));
                dialogEle.css("top", String(dialogOldPositions?.Y ?? 0));
                dialogInstance.current!.height = DefaultMiniMapHeight;
                dialogInstance.current!.dataBind();
                handleDialogResize(null, true);
                $('.e-dialog .e-south-east').css("display", "block");
                setminState(false);
                setFullScreen(false);
            }
        } else {
            dialogInstance.current!.show(false);
            dlg.classList.remove('dialog-maximized');
            dlg.classList.add('dialog-minimized');
            dlg.querySelector('.e-dlg-content')?.classList.add('hide-content');
            if (minimizeIcon) {
                minimizeIcon.className = "e-icons sf-icon-Minimize";
                minimizeIcon.removeAttribute('title');
            }
            dialogInstance.current!.height = 30;
            dialogEle.css("left", "5px");
            dialogEle.css("top", height - 35);
            dialogInstance.current!.dataBind();
            $('.e-dialog .e-south-east').css("display", "none");
            setminState(true);
            setmaxState(false);
            setFullScreen(false);
        }
        handleDialogResize(null, true);
    };

    const onCloseButtonClick = (_e: any) => {
        viewerActions.setMiniMapOpen(false);
    };

    React.useEffect(() => {
        if (showMap) {
            window.dispatchEvent(new Event('resize'));
        }
    }, [showMap]);

    React.useEffect(() => {
        // MINIMAL FIX: guard setState after unmount
        let mounted = true;
        //if (ViewerStore.miniMapSize()) setMimiMapSize(ViewerStore.miniMapSize());
        const handleViewerStoreChange = () => {
            if (!mounted) return;
            setShowMap(ViewerStore.isMiniMapOpen());
            setdialogPositions(ViewerStore.miniMapPositions());
            setMimiMapSize(ViewerStore.miniMapSize());
            setFullScreen(ViewerStore.miniMapFullScreen())
        };

        ViewerStore.addChangeListener(handleViewerStoreChange);

        return () => {
            mounted = false;
            ViewerStore.removeChangeListener(handleViewerStoreChange);
        };
    }, []);

    return (
        <div className="facility-mini-map" id="facility-mini-map">
            <DialogComponent
                id="mini-map-dialog"
                resizing={handleDialogResize}
                beforeOpen={onBeforeOpen}
                created={applyFullScreenState}
                target={props.targetId}
                header={header}
                width={DefaultMiniMapWidth}
                height={DefaultMiniMapHeight}
                allowDragging={true}
                dragStop={onDragStop}
                animationSettings={settings}
                enableResize={true}
                resizeHandles={['All']}
                showCloseIcon={false}
                ref={dialogInstance}
                visible={showMap}
                position={dialogPositions ?? { X: 5, Y: 5 }}
                zIndex={5001}
            >
                <div
                    id="mini-map-content"
                    className='dialog-content mini-map-content'
                    style={{ width: DefaultMiniMapWidth, height: (DefaultMiniMapHeight - 45) }}
                >
                    <FacilityMap
                        siteMap={props.siteMap}
                        views={props.views}
                        isMiniMap={true}
                        activeView={props.activeView}
                        highlightedView={props.highlightedView}
                        showElements={fullScreen}
                        onViewMarkerClicked={onViewMarkerClicked}
                        onViewMarkerHighlighted={props.onViewMarkerHighlighted}
                        resizeCount={resizeCount}
                        viewerDirectionDegrees={props.viewerDirectionDegrees}
                        overridePosition={props.overridePosition}
                    />
                </div>
            </DialogComponent>

            {/*<FabComponent
                ref={mapBtnInstance}
                id='mapButton'
                iconCss='e-icons e-map'
                cssClass={`speed-dial-btn ${speedDialClass}`}
                target="#PanoContainer"
                onClick={restoreMap}
                visible={!showMap}
                title={'Restore' }
            ></FabComponent>*/}
        </div>
    );
};

export default FacilityMiniMap;
